import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format, parseISO } from 'date-fns';

interface DateDistributionChartProps {
  timeSeries: Array<{
    date?: string;
    period?: string;
    votes?: number;
    total?: number;
    upVotes: number;
    downVotes: number;
  }>;
  isLoading?: boolean;
}

export function DateDistributionChart({ timeSeries, isLoading }: DateDistributionChartProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="h-4 bg-gray-200 rounded animate-pulse mb-6"></div>
        <div className="h-64 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  // Process and sort data by date
  const chartData = timeSeries
    .map(item => {
      const dateString = item.date || item.period || new Date().toISOString();
      return {
        ...item,
        date: dateString,
        formattedDate: format(parseISO(dateString), 'MMM dd, HH:mm'),
        upVotes: item.upVotes || 0,
        downVotes: item.downVotes || 0,
        total: item.total || item.votes || (item.upVotes + item.downVotes) || 0
      };
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: entry.color }}></span>
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Conversion: {payload[0]?.payload?.total > 0 
                ? ((payload.find((p: any) => p.dataKey === 'upVotes')?.value || 0) / payload[0].payload.total * 100).toFixed(1)
                : '0'}%
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const totalVotes = chartData.reduce((sum, item) => sum + item.total, 0);
  const avgVotesPerPeriod = chartData.length > 0 ? Math.round(totalVotes / chartData.length) : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Vote Trends Over Time</h3>
          <p className="text-sm text-gray-600">Track feedback patterns and engagement</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-green-600">📈</div>
          <div className="text-xs text-gray-500 mt-1">
            Avg: {avgVotesPerPeriod}/period
          </div>
        </div>
      </div>

      {chartData.length > 0 ? (
        <div className="space-y-6">
          {/* Main time series chart */}
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="upVotesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0.05}/>
                  </linearGradient>
                  <linearGradient id="downVotesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="formattedDate" 
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="upVotes"
                  stroke="#10B981"
                  strokeWidth={2}
                  fill="url(#upVotesGradient)"
                  name="Positive Votes"
                />
                <Area
                  type="monotone"
                  dataKey="downVotes"
                  stroke="#EF4444"
                  strokeWidth={2}
                  fill="url(#downVotesGradient)"
                  name="Negative Votes"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Summary stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-100">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{totalVotes.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Total Votes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {chartData.reduce((sum, item) => sum + item.upVotes, 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Positive</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {chartData.reduce((sum, item) => sum + item.downVotes, 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Negative</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">📈</div>
          <p className="text-gray-500">No time series data available yet</p>
          <p className="text-sm text-gray-400">Data will appear as feedback is received over time</p>
        </div>
      )}
    </div>
  );
} 